import "@aws-amplify/ui-react/styles.css";
import "./src/styles/global.css";
import "./src/styles/main.css";
import "./src/styles/main.scss";
// Must load after the legacy sheets above — it overrides them in dark mode only.
import "./src/theme/legacyDarkFixes.css";
import {
  clearImpersonationHandoffParam,
  consumeImpersonationHandoff,
} from "./src/helpers/impersonation";

export { wrapPageElement } from "./wrappers";

/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

const APPLE_APP_ID = "6449650887";
// The mobile app's iOS custom URL scheme. NOTE: the native iOS build registers
// `io.engagedai.mobileapp` in Info.plist CFBundleURLTypes (NOT the Expo app.json
// "scheme" of com.socialminingai.mobileapp). Keep this in sync with Info.plist.
const IOS_APP_SCHEME = "io.engagedai.mobileapp";
// iOS custom-scheme hand-off is disabled until the native app ships
// `application:openURL:options:` in AppDelegate.mm — without it a scheme URL
// opens the app but never reaches JS Linking (so it won't route), and on a
// device without the app it throws Safari's "address is invalid" dialog.
// iOS installed→open is already covered by Universal Links; not-installed→store
// by the Smart App Banner. Flip to true once the AppDelegate method lands.
const IOS_SCHEME_HANDOFF_ENABLED = false;
// Android package (smai-mobile-app app.json "android.package" + AndroidManifest).
const ANDROID_PACKAGE = "com.socialminingai.mobileapp";
// Store URLs for the "not installed" install path.
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
const APP_STORE_URL = `https://apps.apple.com/app/id${APPLE_APP_ID}`;
// Paths eligible for the native-app hand-off. Only the activity-center deep
// link qualifies: a raw /shorten/<code> is NOT included because we don't yet
// know what it resolves to — src/pages/shorten.js resolves the code and, only
// when it maps to an activity-center URL, does a full-page load of that URL so
// this hand-off fires. Shorten links that resolve elsewhere never open the app.
const DEEP_LINK_PREFIXES = ["/engagements/activity-center"];

const isIOS = () =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
const isAndroid = () => typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);
const isMobileBrowser = () => isIOS() || isAndroid();

const isDeepLinkPath = pathname => DEEP_LINK_PREFIXES.some(prefix => pathname.startsWith(prefix));

// Build the Android App Links intent URL for the current https deep link. Using
// an intent:// URI (not a raw custom scheme, which Chrome rejects with
// ERR_UNKNOWN_URL_SCHEME) lets the OS open the installed app OR, when it's not
// installed, follow browser_fallback_url to the Play Store. scheme=https + the
// package matches the app's autoVerify'd App Link intent-filter exactly.
const buildAndroidIntentUrl = href => {
  const { host, pathname, search, hash } = new URL(href);
  const fallback = encodeURIComponent(PLAY_STORE_URL);
  return `intent://${host}${pathname}${search}${hash}#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
};

// Best-effort: when a deep-link URL is opened *cold* in a mobile browser, hand
// off to the installed app immediately (no tap). Platform-correct per OS:
//   • Android — an intent:// URI with a Play Store browser_fallback_url, so the
//     OS opens the app when installed and bounces to the store when it isn't.
//   • iOS — swap https: -> the app's custom scheme, preserving host/path/query
//     so the app's URL parser matches the Universal Link. (Genuine Universal
//     Links tapped from Messages open the app before the page ever loads; this
//     covers the in-Safari, same-domain case where iOS won't fire the UL.)
// Guarded to run at most once per URL per tab session so a not-installed device
// that returns to this page isn't caught in a redirect loop. Returns true when
// a hand-off was attempted (so the caller can skip the fallback banner).
const tryOpenInstalledApp = () => {
  if (typeof window === "undefined" || !isMobileBrowser()) return false;
  const { pathname, href } = window.location;
  if (!isDeepLinkPath(pathname)) return false;
  const guardKey = `smaiAppOpenTried:${href}`;
  try {
    if (sessionStorage.getItem(guardKey)) return false;
    sessionStorage.setItem(guardKey, "1");
  } catch (e) {
    // sessionStorage unavailable (private mode / blocked) — proceed without the guard.
  }
  if (isAndroid()) {
    window.location.href = buildAndroidIntentUrl(href);
  } else if (isIOS() && IOS_SCHEME_HANDOFF_ENABLED) {
    window.location.href = href.replace(/^https?:/, `${IOS_APP_SCHEME}:`);
  } else {
    // iOS with hand-off disabled: nothing to redirect (UL + Smart Banner cover it).
    return false;
  }
  return true;
};

// We render our own dismissible install bar on deep-link pages for BOTH platforms.
// Android never had an OS-level app-install banner (Chrome dropped it), and iOS's
// native Smart App Banner (the apple-itunes-app meta) only renders in real Safari —
// NOT in the in-app webviews (SFSafariViewController / WKWebView) or Chrome-iOS where
// SMS/social links usually open, so an iOS user tapping a link there saw nothing.
// A JS-injected bar works in every one of those contexts, so we own the banner on
// both platforms instead of relying on the OS. The button opens the app if installed
// (Android intent://) and otherwise routes to the platform's app store.
const INSTALL_BANNER_ID = "smai-app-install-banner";
const INSTALL_BANNER_DISMISS_KEY = "smaiAppInstallBannerDismissed";
// EngagedAi logo, served from static/ at the site root.
const APP_ICON_URL = "/images/icon.png";

const renderInstallBanner = () => {
  if (typeof document === "undefined" || !isMobileBrowser()) return;
  if (!isDeepLinkPath(window.location.pathname)) return;
  if (document.getElementById(INSTALL_BANNER_ID)) return;
  try {
    if (localStorage.getItem(INSTALL_BANNER_DISMISS_KEY)) return;
  } catch (e) {
    // localStorage unavailable — show the banner anyway (can't read the dismiss flag).
  }

  // Dark "smart banner": [×] [icon] [EngagedAi / subtitle] [OPEN], mirroring the
  // iOS Smart App Banner styling so both platforms look consistent.
  const bar = document.createElement("div");
  bar.id = INSTALL_BANNER_ID;
  bar.setAttribute("role", "banner");
  bar.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "right:0",
    "z-index:2147483647",
    "display:flex",
    "align-items:center",
    "gap:10px",
    "padding:8px 12px",
    "background:#1c1c1e",
    "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    "box-shadow:0 1px 6px rgba(0,0,0,0.35)",
  ].join(";");

  const dismissBtn = document.createElement("button");
  dismissBtn.type = "button";
  dismissBtn.setAttribute("aria-label", "Dismiss");
  dismissBtn.textContent = "×";
  dismissBtn.style.cssText = [
    "flex:none",
    "border:0",
    "background:transparent",
    "color:#8e8e93",
    "font-size:20px",
    "line-height:1",
    "padding:0 2px",
    "cursor:pointer",
  ].join(";");
  dismissBtn.addEventListener("click", () => {
    try {
      localStorage.setItem(INSTALL_BANNER_DISMISS_KEY, "1");
    } catch (e) {
      // ignore — banner just reappears next load if we can't persist the flag.
    }
    bar.remove();
  });

  const icon = document.createElement("img");
  icon.src = APP_ICON_URL;
  icon.alt = "EngagedAi";
  icon.style.cssText = "flex:none;width:40px;height:40px;border-radius:9px;object-fit:contain";

  const textWrap = document.createElement("div");
  textWrap.style.cssText = "flex:1;min-width:0;display:flex;flex-direction:column;line-height:1.25";

  const title = document.createElement("span");
  title.textContent = "EngagedAi";
  title.style.cssText = "color:#fff;font-size:15px;font-weight:600";

  const subtitle = document.createElement("span");
  subtitle.textContent = "Open in the EngagedAi app";
  subtitle.style.cssText =
    "color:#8e8e93;font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis";

  textWrap.appendChild(title);
  textWrap.appendChild(subtitle);

  const openBtn = document.createElement("button");
  openBtn.type = "button";
  openBtn.textContent = isIOS() ? "GET" : "OPEN";
  openBtn.style.cssText = [
    "flex:none",
    "border:0",
    "border-radius:18px",
    "padding:7px 18px",
    "background:#0a84ff",
    "color:#fff",
    "font-weight:700",
    "font-size:13px",
    "letter-spacing:0.3px",
    "cursor:pointer",
  ].join(";");
  openBtn.addEventListener("click", () => {
    // Read the live URL at click time so SPA navigation is reflected.
    if (isIOS()) {
      // No reliable install check inside a webview; an installed device is already
      // intercepted by the Universal Link before this page loads, so send to the
      // App Store (which itself shows "OPEN" if the app happens to be installed).
      window.location.href = APP_STORE_URL;
    } else {
      // intent:// opens the app if installed, else follows the Play Store fallback.
      window.location.href = buildAndroidIntentUrl(window.location.href);
    }
  });

  bar.appendChild(dismissBtn);
  bar.appendChild(icon);
  bar.appendChild(textWrap);
  bar.appendChild(openBtn);
  document.body.appendChild(bar);
};

export const onClientEntry = () => {
  // EAI-1486: admin impersonation opens in a new tab, which needs the target
  // moved from the cross-tab handoff into this tab's sessionStorage before
  // React/Apollo boot — otherwise the first ME query runs as the admin.
  consumeImpersonationHandoff();
  // Only on the initial (cold) load — not on in-app SPA navigation, so a
  // logged-in user browsing the web app is never pulled into the native app.
  tryOpenInstalledApp();
  // Custom install/open bar for iOS + Android. Works inside in-app webviews,
  // unlike the native iOS Smart App Banner. Harmless if the hand-off above
  // already navigated away; visible if it didn't (or the user returns without
  // installing).
  renderInstallBanner();
};

export const onInitialClientRender = () => {
  // Drop the impersonation handoff token from the URL once Gatsby has rendered.
  // Doing this any earlier (e.g. in onClientEntry) breaks Gatsby's initial
  // page-resource lookup, which is keyed by pathname + search.
  clearImpersonationHandoffParam();
};
